import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Table } from 'react-bootstrap'
import { formatRelative } from 'date-fns'
import { useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faShare } from '@fortawesome/pro-solid-svg-icons'
import { IMainState } from 'store/states'
import { firstLetterUppercase } from 'helpers/converters.helper'
import UserAvatar from 'components/ui/user-avatar/UserAvatar'
import { getLanguage } from 'helpers/date.helper'
import Loader from 'components/ui/loader/Loader'
import ChatModal from '../chatModal/ChatModal'
import styles from './ChatTable.module.scss'

interface IChatTable {
  onSend: any
}

const ChatTable: React.FC<IChatTable> = ({ onSend }) => {
  const { t } = useTranslation()
  const [showChat, setshowChat] = useState(0)
  const [conversations, setConversations] = useState([])
  const [modalTitle, setModalTitle] = useState('')
  const [conversationOwner, setConversationOwner] = useState(0)
  const [conversationId, setConversationId] = useState(0)
  const { loading } = useSelector(({ chat }: IMainState) => chat)
  const { conversationsList } = useSelector(({ chat }: IMainState) => chat)

  useEffect(() => {
    setConversations(conversationsList)
  }, [conversationsList])

  const truncateText = (text: string, chars: number) => {
    if (!text) return ''
    if (text.length < chars) return text

    const lastSpace = text.slice(0, chars).lastIndexOf(' ')
    if (lastSpace === -1) return text.slice(0, chars) + '...'

    return text.substring(0, lastSpace) + '...'
  }

  const getAvatar = (user: any) => {
    return !user.identifier
      ? {
          fullName: t('components.chat.admin'),
          position: t('components.chat.admin'),
          avatar: undefined,
        }
      : { ...user, position: truncateText(user.position, 35) }
  }

  const handleCourseSelected = (
    conversationId: number,
    courseId: number,
    name: string,
    owner: number
  ) => {
    setshowChat(courseId)
    setModalTitle(name)
    setConversationOwner(owner)
    setConversationId(conversationId)
  }

  const handleSubmit = (data: any) => {
    onSend(data)
  }

  return (
    <div className="w-100 h-100">
      {showChat === 0 && loading ? (
        <Loader />
      ) : (
        <Table responsive className={styles.table}>
          <thead>
            <tr>
              <th>{t('components.chat.chatTable.headers.name')}</th>
              <th>{t('components.chat.chatTable.headers.course')}</th>
              <th>{t('components.chat.chatTable.headers.messageReceived')}</th>
              <th>{t('components.chat.chatTable.headers.date')}</th>
            </tr>
          </thead>
          <tbody>
            {conversations.map(
              (
                {
                  conversationId,
                  courseId,
                  owner,
                  courseName,
                  userToShow,
                  lastMessage,
                }: any,
                index
              ) => (
                <tr
                  className={
                    !lastMessage.read && !lastMessage.owner
                      ? styles.newMessage
                      : ''
                  }
                  key={
                    'conversation-' +
                    courseId +
                    '-' +
                    courseName +
                    '-' +
                    owner +
                    '-' +
                    index
                  }
                  onClick={() =>
                    handleCourseSelected(
                      conversationId,
                      courseId,
                      courseName,
                      owner
                    )
                  }
                >
                  <td className={styles.avatar}>
                    <UserAvatar
                      className={styles.avatarText}
                      user={getAvatar(userToShow)}
                    />
                  </td>
                  <td className={styles.course}>
                    {' '}
                    {truncateText(courseName, 55)}
                  </td>
                  <td className={styles.message}>
                    <div>
                      {lastMessage.owner && (
                        <FontAwesomeIcon
                          icon={faShare}
                          className="mr-2"
                        ></FontAwesomeIcon>
                      )}
                      {truncateText(lastMessage.content, 55)}
                    </div>
                  </td>
                  <td className={styles.date}>
                    {firstLetterUppercase(
                      formatRelative(
                        new Date(lastMessage.createdAt),
                        new Date(),
                        { locale: getLanguage() }
                      )
                    )}
                    {!lastMessage.read && !lastMessage.owner && (
                      <div className={styles.newMessageDot}></div>
                    )}
                  </td>
                </tr>
              )
            )}
          </tbody>
        </Table>
      )}
      {showChat > 0 && (
        <ChatModal
          show={showChat > 0}
          courseId={showChat}
          conversationId={conversationId}
          title={modalTitle}
          owner={conversationOwner}
          onClose={() => setshowChat(0)}
          onSend={handleSubmit}
        />
      )}
    </div>
  )
}

export default ChatTable
